<template>
  <div
    class="flex justify-center clamp-gap-4-6"
    :data-many-scroller="inviewAlwaysEvent"
    :data-many-scroller-event-id="eventId"
  >
    <div
      ref="containerRef"
      class="scroll-snap-container overflow-x-scroll flex justify-start hide-scrollbars"
      :class="[
        'py-175px my--175px', // to prevent overflow-x-scroll from cropping tooltips
      ]"
      @scroll="updateArrowVisibility"
    >
      <div
        class="flex flex-row items-center justify-center"
        :class="{
          'clamp-gap-4-6': spacing === 'md',
        }"
      >
        <slot />
      </div>
    </div>
    <AIconButton
      v-if="showArrows"
      icon="arrow-left"
      size="sm"
      ariaLabel="Go to previous"
      :disabled="!disableLeftArrow"
      class="!absolute top-50% left-0 translate-y--50% clamp-mx-3-5 z-character-tooltip"
      @click="
        onInteraction();
        scrollByDirection('left');
      "
    />
    <AIconButton
      v-if="showArrows"
      icon="arrow-right"
      size="sm"
      ariaLabel="Go to next"
      :disabled="!disableRightArrow"
      class="!absolute top-50% right-0 translate-y--50% clamp-mx-3-5 z-character-tooltip"
      @click="
        onInteraction();
        scrollByDirection('right');
      "
    />
  </div>
</template>

<script setup lang="ts">
import { useWindowSize } from '@vueuse/core';
import { useManyScrollerOptions } from '@/composables/useManyScrollerOptions';

const { inviewAlwaysEvent } = useManyScrollerOptions();
const { width: windowWidth } = useWindowSize();

interface Props {
  id: string;
  itemCount: number;
  spacing?: 'none' | 'md';
  startPosition?: 'start' | 'center' | 'end';
  autoScroll?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  spacing: 'md',
  startPosition: 'start',
  autoScroll: false,
});

const containerRef = ref<HTMLElement | null>(null);
const showArrows = ref(false);
const disableLeftArrow = ref(false);
const disableRightArrow = ref(false);
const hasInteracted = ref(false);

const round = (value: number) => Math.round(value * 10000) / 10000;
const easeInOutSine = (x: number) => -(Math.cos(Math.PI * x) - 1) / 2;
const min = 0;
const max = 1;
const speed = computed(() => 0.3 / scrollDistance.value); // speed feels the same regardless of the container scroll width
const animationRaF = ref<number | null>(null);
const scrollDistance = ref(0);
const scrollDirection = ref<'left' | 'right'>('right');
const scrollPosition = ref(0);
const scrollPositionEased = computed(() =>
  round(easeInOutSine(scrollPosition.value)),
);
const onInteraction = () => (hasInteracted.value = true);

// use inView to pause and play animation
const inView = ref(false);
const onEnter = () => (inView.value = true);
const onLeave = () => (inView.value = false);
const eventId = computed(() => `event-carousel-${props.id}`);
const shouldAnimate = computed(
  () =>
    props.autoScroll &&
    inView.value &&
    showArrows.value &&
    !hasInteracted.value,
);

// smooth animate scroll position value between 0 - 1 infinite
const runAnimation = () => {
  if (scrollDirection.value === 'left') {
    scrollPosition.value -= speed.value;
    if (scrollPosition.value <= min) scrollDirection.value = 'right';
  } else {
    scrollPosition.value += speed.value;
    if (scrollPosition.value >= max) scrollDirection.value = 'left';
  }
  animationRaF.value = requestAnimationFrame(runAnimation);
};
const stopAnimation = () => {
  if (animationRaF.value) cancelAnimationFrame(animationRaF.value);
};

// control start / stop animation
watch(
  shouldAnimate,
  (newVal) => {
    if (newVal) {
      if (animationRaF.value) {
        // animation has been paused - start from last scroll position set by user interaction
        const scrollLeft = containerRef.value?.scrollLeft || 0;
        scrollPosition.value = scrollLeft / scrollDistance.value;
      }
      runAnimation();
    } else stopAnimation();
  },
  { immediate: true },
);

// reset has interacted when carousel is out of view, to restart auto scroll
watch(inView, (newVal) => {
  if (!newVal) hasInteracted.value = false;
});

// update actual scroll position when eased value changes
watch(scrollPositionEased, () => {
  if (containerRef.value) {
    containerRef.value.scrollTo({
      left: scrollDistance.value * scrollPositionEased.value,
    });
  }
});

// update arrow visibility based on container size
const updateArrowVisibility = () => {
  if (!containerRef.value) return;
  const container = containerRef.value;

  scrollDistance.value = container.scrollWidth - windowWidth.value;
  showArrows.value = container.scrollWidth > windowWidth.value;

  disableLeftArrow.value = container.scrollLeft > 0;
  disableRightArrow.value =
    container.scrollLeft < container.scrollWidth - container.offsetWidth;
};

// scroll the carousel when clicking the arrow buttons
const scrollByDirection = (direction: 'left' | 'right') => {
  if (!containerRef.value) return;

  const container = containerRef.value;
  let scrollAmount = windowWidth.value / 2;

  if (direction === 'left') {
    if (scrollAmount > container.scrollLeft)
      scrollAmount = container.scrollLeft;
    container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
  } else if (direction === 'right') {
    if (container.scrollLeft + scrollAmount > scrollDistance.value)
      scrollAmount = scrollDistance.value - container.scrollLeft;
    container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  }
};

onMounted(() => {
  if (props.startPosition === 'center') {
    scrollPosition.value = 0.35; // start a bit before the center
    containerRef.value?.scrollTo({
      left: containerRef.value.scrollWidth / 2 - windowWidth.value / 2,
    });
  } else if (props.startPosition === 'end') {
    scrollPosition.value = 1;
    containerRef.value?.scrollTo({
      left: containerRef.value.scrollWidth - windowWidth.value,
    });
  }

  updateArrowVisibility();

  window.addEventListener('resize', updateArrowVisibility);
  document.addEventListener(`${eventId.value}-full-enter`, onEnter);
  document.addEventListener(`${eventId.value}-full-exit`, onLeave);

  // animate scroll position until the user interacts with the carousel
  if (props.autoScroll)
    containerRef.value?.addEventListener('pointerdown', onInteraction);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateArrowVisibility);
  document.removeEventListener(`${eventId.value}-full-enter`, onEnter);
  document.removeEventListener(`${eventId.value}-full-exit`, onLeave);

  if (props.autoScroll) {
    stopAnimation();
    containerRef.value?.removeEventListener('pointerdown', stopAnimation);
  }
});
</script>

<style scoped lang="scss">
.scroll-snap-container {
  scroll-snap-type: x mandatory;
}
.scroll-snap-item {
  scroll-snap-align: center;
}
</style>
